import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { DEFAULT_NEW_APP_ROUTE } from '@/modules/common/application/route-fallback';

import { ListProject } from '../types/Project';

interface DeleteProjectDialogProps {
  project: ListProject;
  open: boolean;
  deleteLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (id: string) => void;
}

export const DeleteProjectDialog = ({
  project,
  open,
  deleteLoading,
  onOpenChange,
  onDelete: deleteProject,
}: DeleteProjectDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmationText, setConfirmationText] = useState('');
  const [deletionInitiated, setDeletionInitiated] = useState(false);

  useEffect(() => {
    if (deletionInitiated && !deleteLoading) {
      onOpenChange(false);
      setDeletionInitiated(false);
    }
  }, [deleteLoading, deletionInitiated, onOpenChange]);

  const handleDelete = async () => {
    try {
      const projectId = project.id;
      setDeletionInitiated(true);
      deleteProject(projectId);
      if (location.pathname.includes(`/project/${projectId}`)) {
        navigate(DEFAULT_NEW_APP_ROUTE);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      setDeletionInitiated(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setConfirmationText('');
      setDeletionInitiated(false);
    }
  };

  const isDeleteEnabled = confirmationText === project.title && !deleteLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('modules.mavue2.project.delete.title')}</DialogTitle>
        </DialogHeader>

        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
          <div className="flex items-start">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="ml-2">
              <p className="text-sm text-yellow-800">
                {t('modules.mavue2.project.delete.warning')}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmation-input">
            <Trans
              i18nKey="modules.mavue2.project.delete.confirmLabel"
              values={{ title: project.title }}
              components={{ strong: <span className="font-semibold" /> }}
            />
          </Label>
          <Input
            id="confirmation-input"
            type="text"
            placeholder={t('modules.mavue2.project.delete.confirmPlaceholder', {
              title: project.title,
            })}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            disabled={deleteLoading}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={deleteLoading}
          >
            {t('modules.mavue2.common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={!isDeleteEnabled}>
            {deleteLoading ? (
              <>
                <Spinner className="mr-2" />
              </>
            ) : (
              t('modules.mavue2.project.delete.submit')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
